import { ReactNode } from 'react';
import { useAuth } from './useAuth';

interface PermissionGuardProps {
  children: ReactNode;
  permission: string | string[];
  requireAll?: boolean;
  fallback?: ReactNode;
}

/**
 * Protects components/routes that require specific permissions.
 * Can check for single permission or multiple permissions.
 */
export const PermissionGuard = ({
  children,
  permission,
  requireAll = false,
  fallback = null,
}: PermissionGuardProps) => {
  const { hasPermission, isLoading, featuresLoaded } = useAuth();

  // Wait for both the OIDC user AND the application-configuration fetch that
  // populates permissions — deciding on an empty permission set would render
  // the fallback (often a redirect) for users who actually have access.
  if (isLoading || !featuresLoaded) {
    return null;
  }

  const permissions = Array.isArray(permission) ? permission : [permission];

  const hasAccess = requireAll
    ? permissions.every((p) => hasPermission(p))
    : permissions.some((p) => hasPermission(p));

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
